import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { APPLY_URL, HFA_WHATSAPP, INSTAGRAM } from "../../lib/site-data";
import SectionLabel from "./SectionLabel";
import WhatsAppMark from "./WhatsAppMark";
import InstagramMark from "./InstagramMark";

const waMessage = encodeURIComponent(
  "Hey! Can you please share more about High Five Access?"
);

export default function Contact() {
  const waNumber = HFA_WHATSAPP.replace(/\D/g, "");
  const waLink = `https://wa.me/91${waNumber}?text=${waMessage}`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section-pad bg-[#0A0A0A] text-white relative overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-orange-600/15 blur-[120px] pointer-events-none"
      />
      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <SectionLabel dark>Contact</SectionLabel>
          <h2
            data-testid="contact-headline"
            className="font-display mt-8 text-xl md:text-2xl font-bold tracking-tightest leading-[1.05]"
          >
            Questions or suggestions?
            <br />
            We&rsquo;re just a message away.
          </h2>
          <p className="mt-5 text-base md:text-lg text-white/65 leading-relaxed">
            We&rsquo;d love to hear from you. Your suggestions help us make
            High Five Access even better for everyone.
          </p>
        </motion.div>

        {/* Clean horizontal rectangle cards */}
        <div className="mt-10 md:mt-12 flex flex-col gap-3 max-w-3xl">
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp"
            className="group flex items-center gap-4 sm:gap-5 rounded-2xl border border-white/10 bg-white/[0.03] px-4 sm:px-6 py-3.5 sm:py-5 hover:border-[#25D366]/50 hover:bg-white/[0.06] transition-all duration-300"
          >
            <div className="shrink-0">
              <WhatsAppMark size={44} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/45 font-semibold">
                WhatsApp
              </div>
              <div className="font-display mt-0.5 text-base sm:text-lg md:text-xl font-semibold tracking-tight truncate">
                +91 {HFA_WHATSAPP}
              </div>
            </div>
            <div className="shrink-0 inline-flex items-center gap-1.5 text-sm text-[#25D366] font-medium">
              <span className="hidden sm:inline">Message</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </a>

          <a
            href={INSTAGRAM.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-instagram"
            className="group flex items-center gap-4 sm:gap-5 rounded-2xl border border-white/10 bg-white/[0.03] px-4 sm:px-6 py-3.5 sm:py-5 hover:border-[#C837AB]/40 hover:bg-white/[0.06] transition-all duration-300"
          >
            <div className="shrink-0">
              <InstagramMark size={44} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/45 font-semibold">
                Instagram
              </div>
              <div className="font-display mt-0.5 text-base sm:text-lg md:text-xl font-semibold tracking-tight whitespace-nowrap overflow-hidden text-ellipsis">
                {INSTAGRAM.handle}
              </div>
            </div>
            <div className="shrink-0 inline-flex items-center gap-1.5 text-sm text-orange-500 font-medium">
              <span className="hidden sm:inline">Follow</span>
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
          </a>

          <Link
            to={APPLY_URL}
            data-testid="contact-apply"
            className="group flex items-center gap-4 sm:gap-5 rounded-2xl bg-orange-600 hover:bg-orange-700 text-white px-4 sm:px-6 py-3.5 sm:py-5 transition-all duration-300"
          >
            <div className="shrink-0 w-11 h-11 rounded-xl bg-white/15 text-white flex items-center justify-center">
              <ArrowUpRight className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/80 font-semibold">
                Membership
              </div>
              <div className="font-display mt-0.5 text-base sm:text-lg md:text-xl font-semibold tracking-tight">
                Apply for Membership
              </div>
            </div>
            <div className="shrink-0 inline-flex items-center gap-1.5 text-sm text-white/90 font-medium">
              <span className="hidden sm:inline">~3 min</span>
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
